import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { Button, Loader, Modal } from '../../../../Components/uielements';
import {
  OutlineButton,
  ModalButtonWrapper,
  ModalGap,
} from '../../../../services/settings/appStyle/global.style';

import { Form } from '@kuzmycz/react-form-ula';
import BioPlusTextField from '../../../../Components/form-fields/BioPlusTextField';

import {
  createTemplate,
  updateTemplate,
} from '../../../../services/bioplus-services/emailTemplate';

import { ModalLabel } from '../../UserV2/user.style';
import { quillModules, quillFormats } from '../config/quillConfig';
import { buildEmailHtml } from '../config/buildEmailHtml';
import { ModalWrapper } from '../emailTemplate.style';

const CreateTemplateModal = ({ onClose, templateData, isEdit = false }) => {
  const queryClient = useQueryClient();
  const quillRef = useRef(null);

  const [content, setContent] = useState(isEdit ? templateData?.template || '' : '');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const templateMutation = useMutation(
    (payload) =>
      isEdit && templateData?._id
        ? updateTemplate({ ...payload, templateId: templateData._id })
        : createTemplate(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('emailTemplates');
        setSuccess(true);
      },
      onError: (e) => {
        setError(e?.response?.data?.message || 'Failed to save template');
      },
    }
  );

  const handleSubmit = (values) => {
    if (!content || content === '<p><br></p>') {
      setError('Template content is empty');
      return;
    }

    const inlineHtml = buildEmailHtml(content);

    templateMutation.mutate({
      title: values.title,
      subject: values.subject,
      template: inlineHtml,
    });
  };

  return (
    <Modal
      isOpen
      title={isEdit ? 'Edit Email Template' : 'Create Email Template'}
      overFlowVisible={true}
    >
      <ModalWrapper>
        <Form
          initialValues={{
            title: isEdit ? templateData?.title : '',
            subject: isEdit ? templateData?.subject : '',
          }}
          onSubmit={handleSubmit}
        >
          {success ? (
            <ModalGap>
              <ModalLabel>Email template {isEdit ? 'updated' : 'created'} successfully!</ModalLabel>
              <ModalButtonWrapper>
                <Button varient="normal" onClick={onClose}>
                  Close
                </Button>
              </ModalButtonWrapper>
            </ModalGap>
          ) : error ? (
            <ModalGap>
              <ModalLabel style={{ color: 'red' }}>{error}</ModalLabel>
              <ModalButtonWrapper>
                <Button varient="normal" onClick={() => setError(null)}>
                  Try Again
                </Button>
                <OutlineButton onClick={onClose}>Close</OutlineButton>
              </ModalButtonWrapper>
            </ModalGap>
          ) : (
            <>
              <ModalGap>
                <BioPlusTextField name="title" label="Template Title" fullWidth disabled={isEdit} />
                <BioPlusTextField name="subject" label="Email Subject" fullWidth />
              </ModalGap>

              <div className="email-editor" style={{ margin: '30px 0' }}>
                <ReactQuill
                  ref={quillRef}
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your email content here..."
                  theme="snow"
                />
              </div>

              <ModalButtonWrapper>
                <OutlineButton onClick={onClose}>Close</OutlineButton>

                {templateMutation.isLoading ? (
                  <Button varient="normal" disabled>
                    Saving...
                    <Loader size="small" isNotRequiredLoadingText />
                  </Button>
                ) : (
                  <Button varient="normal" type="submit">
                    {isEdit ? 'Update Template' : 'Create Template'}
                  </Button>
                )}
              </ModalButtonWrapper>
            </>
          )}
        </Form>
      </ModalWrapper>
    </Modal>
  );
};

export default CreateTemplateModal;
